import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Zap, Plus, X, History } from "lucide-react";
import client, { errText } from "../../lib/api";
import Stepper from "./Stepper";

// Shown instantly so the palette is never empty. The suggestions
// fetch upgrades this to a field-relevant, personalized set when it
// resolves; if the fetch fails, these stay put. Written in a
// student's voice, not a professional resume line.
const FALLBACK_CHIPS = [
  "led a college event", "did an internship", "started a club",
  "took a part-time job", "learned a skill on my own", "volunteered somewhere",
  "won a competition", "helped a friend through a rough patch",
  "juggled studies and work", "picked up a side hustle",
  "led a team", "bounced back from a setback",
];

/**
 * A7: Experience chips. A curated list of quick-pick chips, plus a
 * free-text add. The chips seed context for Klair; nothing is scored
 * here.
 */
export default function ExperienceChipsScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [suggestions, setSuggestions] = useState(FALLBACK_CHIPS);
  const [remembered, setRemembered] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/onboarding/experience-chips/suggestions");
        if (Array.isArray(data?.chips) && data.chips.length) {
          setSuggestions(data.chips);
        }
        if (Array.isArray(data?.remembered)) {
          setRemembered(data.remembered);
        }
      } catch (_e) {
        // keep the fallback set so the palette is never empty
      }
      try {
        const { data } = await client.get("/onboarding");
        if (Array.isArray(data.experience_chips) && data.experience_chips.length) {
          setChosen(data.experience_chips);
        }
      } catch (_e) { /* first time */ }
    })();
  }, []);

  const toggle = (c) => {
    setChosen((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : (cur.length < 8 ? [...cur, c] : cur));
  };
  const addCustom = () => {
    const t = custom.trim();
    if (!t) return;
    if (chosen.includes(t)) { setCustom(""); return; }
    if (chosen.length >= 8) { setCustom(""); return; }
    setChosen((cur) => [...cur, t]);
    setCustom("");
  };
  const forget = async (c) => {
    // Optimistic: drop it from the palette and from any active picks so
    // a Continue save cannot re-remember it.
    setRemembered((cur) => cur.filter((x) => x !== c));
    setChosen((cur) => cur.filter((x) => x !== c));
    try {
      await client.post("/onboarding/experience-chips/forget", { chip: c });
    } catch (_e) {
      // Silent: the local removal already reflects intent; a failed
      // forget just means it may reappear next visit.
    }
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const existing = await client.get("/onboarding");
      const cur = existing?.data || {};
      await client.put("/onboarding", {
        org_slug: cur.org_slug || null,
        education: cur.education || [],
        experience_chips: chosen,
        goal: cur.goal || "",
        posture: cur.posture || "",
        step_index: 3,
        is_complete: false,
      });
      nav(`${base}/onboarding/goal`);
    } catch (e) {
      setErr(errText(e, "That did not save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="k-shell relative overflow-hidden">
      <div className="k-orb k-orb-teal" style={{ top: "-6rem", left: "-4rem", width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-blue" style={{ bottom: "-6rem", right: "-6rem", width: "22rem", height: "22rem" }} />
      <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-24">
        <Stepper current={2} total={3} />
        <div className="mt-6 k-fade-in">
          <div className="k-chip mb-3"><Zap size={12} /> experience</div>
          <h1 className="klareet-serif mb-3" style={{ fontSize: "clamp(1.7rem, 3.6vw, 2.3rem)", lineHeight: 1.12 }} data-testid="exp-title">
            So, what have you actually done?
          </h1>
          <p className="k-quiet mb-6">Tap whatever fits, up to eight. This just gives me texture, nothing here gets scored. Add your own if I miss something.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-5" data-testid="chip-palette">
          {suggestions.map((c) => {
            const active = chosen.includes(c);
            return (
              <button
                key={c}
                onClick={() => toggle(c)}
                className={active ? "k-pill" : "k-pill"}
                style={active ? {
                  background: "hsl(var(--klareet-blue-soft))",
                  color: "hsl(var(--klareet-blue-strong))",
                  borderColor: "hsl(var(--klareet-blue))",
                  fontWeight: 600,
                } : {}}
                data-testid={`chip-${c.replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            );
          })}
        </div>

        {remembered.filter((c) => !suggestions.includes(c)).length > 0 && (
          <div className="mb-5 k-fade-in" data-testid="remembered-group">
            <div className="k-label mb-2 flex items-center gap-1.5">
              <History size={12} /> Things you've mentioned before
            </div>
            <div className="flex flex-wrap gap-2">
              {remembered.filter((c) => !suggestions.includes(c)).map((c) => {
                const active = chosen.includes(c);
                return (
                  <span
                    key={`rem-${c}`}
                    className="k-pill inline-flex items-center gap-1.5"
                    style={active ? {
                      background: "hsl(var(--klareet-teal-soft, var(--klareet-blue-soft)))",
                      color: "hsl(var(--klareet-teal-strong, var(--klareet-blue-strong)))",
                      borderColor: "hsl(var(--klareet-teal, var(--klareet-blue)))",
                      fontWeight: 600,
                    } : { borderStyle: "dashed" }}
                    data-testid={`remembered-${c.replace(/\s+/g, "-")}`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(c)}
                      className="bg-transparent p-0 border-0 cursor-pointer"
                      style={{ color: "inherit", font: "inherit" }}
                      data-testid={`remembered-add-${c.replace(/\s+/g, "-")}`}
                    >
                      {c}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); forget(c); }}
                      aria-label={`forget ${c}`}
                      title="forget this"
                      className="opacity-50 hover:opacity-100 transition-opacity"
                      style={{ color: "inherit" }}
                      data-testid={`remembered-forget-${c.replace(/\s+/g, "-")}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
            </div>
            <div className="k-quiet !text-xs mt-2">Tap to reuse, or tap the x to forget one for good.</div>
          </div>
        )}

        <div className="k-card k-fade-in" style={{ animationDelay: "120ms" }}>
          <div className="k-label mb-2">Your picks</div>
          {chosen.length === 0 ? (
            <div className="k-quiet" data-testid="chosen-empty">Nothing yet. Tap a few above, or add your own below.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="chosen-list">
              {chosen.map((c) => (
                <span
                  key={c}
                  className="k-chip"
                  style={{ background: "hsl(var(--klareet-blue))", color: "white" }}
                  data-testid={`chosen-${c.replace(/\s+/g, "-")}`}
                >
                  {c}
                  <button onClick={() => toggle(c)} aria-label={`remove ${c}`} style={{ color: "rgba(255,255,255,0.8)" }}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <input
              className="k-input"
              placeholder="something you've done, in your own words"
              value={custom}
              maxLength={80}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
              data-testid="chip-custom"
            />
            <button onClick={addCustom} disabled={!custom.trim() || chosen.length >= 8} className="k-btn k-btn-primary" data-testid="chip-add">
              <Plus size={14} /> Add
            </button>
          </div>
          <div className="k-quiet mt-3 !text-xs">{chosen.length} of 8 selected</div>
        </div>

        {err && <div className="mt-4 k-quiet" style={{ color: "hsl(var(--destructive))" }}>{err}</div>}

        <div className="mt-8 flex justify-between items-center">
          <button onClick={() => nav(`${base}/onboarding/education`)} className="k-btn k-btn-ghost" data-testid="exp-back">
            Back
          </button>
          <button onClick={submit} disabled={chosen.length === 0 || busy} className="k-btn k-btn-accent" data-testid="exp-continue">
            {busy ? "Saving." : "Continue"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
